import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useEffect, useState, useMemo, useRef } from "react";
import SEO from "src/SEO";
import Hero from "src/components/Hero/Hero";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import MarkdownWithRouter from "src/components/MarkdownWithRouter";
import TitleSection from "src/components/TitleSection/TitleSection";
import EventFinished from "src/components/EventFinished";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import DynamicGoogleFormSubmitter from "src/components/Form/DynamicGoogleFormSubmitter";
import Button from "src/components/Button/Button";
import SpinWheelModal from "src/components/SpinWheel/SpinWheelModal";
import wheelConfigDefault from "src/config/wheelConfigDefault";
import { shouldPublish, getScheduleDates } from "src/utils/publishUtils";

// Consolidated promo configuration with route patterns
const PROMO_CONFIG = {
  "sick-podcast": {
    sheetName: "Sick 1",
    seoKey: "sickPodcastFreePlay2026",
    enPattern: "sick-podcast-free-play-2026",
    frPattern: "sick-podcast-credits-jeu-2026",
  },
  "drive-by": {
    sheetName: "Drive 2",
    seoKey: "driveByPodcastFreePlay2026",
    enPattern: "drive-by-podcast-free-play-2026",
    frPattern: "podcast-drive-by-credits-jeu-2026",
  },
  "standing-by": {
    sheetName: "Standing 3",
    seoKey: "standingByPodcastFreePlay2026",
    enPattern: "standing-by-podcast-free-play-2026",
    frPattern: "podcast-standing-by-credits-jeu-2026",
  },
};

const PodcastFreePlayDynamic2026 = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  // Detect promo name from pathname
  const promoName = useMemo(() => {
    const pathname = location.pathname;

    // Check each config to see if the pathname matches
    for (const [key, config] of Object.entries(PROMO_CONFIG)) {
      if (pathname.includes(config.enPattern) || pathname.includes(config.frPattern)) {
        return key;
      }
    }

    return null;
  }, [location.pathname]);
  const [showWheel, setShowWheel] = useState(false);
  const [spinPrize, setSpinPrize] = useState("");
  const formRef = useRef(null);
  const customButtonRef = useRef(null);

  // Validate promo name exists and get config
  const promoConfig = promoName ? PROMO_CONFIG[promoName] : null;

  if (!promoConfig) {
    return (
      <ErrorDiv>
        <h2>{t("podcastFreePlayDynamic2026.common.error.title")}</h2>
        <p>{t("podcastFreePlayDynamic2026.common.error.message")}</p>
      </ErrorDiv>
    );
  }

  const { sheetName, seoKey } = promoConfig;

  // Check if page is published - check both dynamic schedule and fallback to main schedule
  const isPublished = useMemo(() => {
    // First try the dynamic schedule for this specific promo
    const dynamicSchedule = getScheduleDates(`podcastFreePlayDynamic2026_${promoName}`);
    if (dynamicSchedule && Object.keys(dynamicSchedule).length > 0) {
      return shouldPublish(dynamicSchedule);
    }

    // Fall back to main podcastFreePlay2026 schedule
    const mainSchedule = getScheduleDates("podcastFreePlay2026");
    return shouldPublish(mainSchedule);
  }, [promoName]);

  // Move the custom button inside the form container so it gets covered by success overlay
  useEffect(() => {
    const formContainer = formRef.current?.querySelector('.form-container');
    const customButton = customButtonRef.current;

    if (formContainer && customButton && !formContainer.contains(customButton)) {
      formContainer.appendChild(customButton);
    }
  }, []);

  // Build wheel config from default + i18n text strings
  const wheelData = useMemo(() => {
    const wheelTextSegments = t('podcastFreePlayDynamic2026.wheel.segments', { returnObjects: true });

    // Merge structural data with localized text
    const segmentValuesArray = [
      { probability: 0, type: "string", win: true, userData: { prize: 0 }, ...wheelTextSegments[4] },
      { probability: 49, type: "string", win: true, userData: { prize: 10 }, ...wheelTextSegments[3] },
      { probability: 49, type: "string", win: true, userData: { prize: 20 }, ...wheelTextSegments[2] },
      { probability: 0, type: "string", win: true, userData: { prize: 0 }, ...wheelTextSegments[4] },
      { probability: 1, type: "string", win: true, userData: { prize: 50 }, ...wheelTextSegments[1] },
      { probability: 1, type: "string", win: true, userData: { prize: 100 }, ...wheelTextSegments[0] }
    ];

    // Custom color array to match number of segments
    const colorArray = [
      "#2d2d2d",  // $0
      "#57a96f",  // $10
      "#da6a3e",  // $20
      "#2d2d2d",  // $0
      "#6bb3a1",  // $50
      "#fcb441"   // $100
    ];

    return {
      ...wheelConfigDefault,
      segmentValuesArray,
      colorArray,
      centerCircleFillColor: "#000",
      centerCircleStrokeColor: "#F39C12",
      introText: ''
    };
  }, [t]);

  // Custom submit handler that shows wheel first
  const handleCustomSubmit = () => {
    // Validate form first
    const form = formRef.current?.querySelector('form');
    if (form && !form.checkValidity()) {
      // Trigger HTML5 validation
      form.reportValidity();
      return;
    }

    // Show wheel modal
    setShowWheel(true);
  };

  // Handle prize won - set prize and auto-submit
  const handlePrizeWon = (prize) => {
    setSpinPrize(String(prize));
    // Don't close modal - keep it open
    // setShowWheel(false);

    // Auto-submit the form after a brief delay
    setTimeout(() => {
      const form = formRef.current?.querySelector('form');
      if (form) {
        console.log('Prize won, submitting form with prize:', prize);
        form.requestSubmit();
      }
    }, 500);
  };

  return (
    <PodcastFreePlayDiv>
      <SEO />
      <EventFinished
        scheduleKeyName={'podcastFreePlay2026'}
        eventType="promotion"
      />
      <Hero
        bgImage={t(`podcastFreePlayDynamic2026.${promoName}.hero.bgImage`)}
        image={t(`podcastFreePlayDynamic2026.${promoName}.hero.logoImage`)}
        imageAlt={t(`podcastFreePlayDynamic2026.${promoName}.hero.alt`)}
        description={t(`podcastFreePlayDynamic2026.${promoName}.title`)}
        fluidImage={false}
      />

      <MainContentDiv>
        <container-div>
          <TitleSection
            title={t(`podcastFreePlayDynamic2026.${promoName}.subtitle`)}
            text={t(`podcastFreePlayDynamic2026.${promoName}.subsubtitle`)}
            dark={false}
          />
          <DescriptionText>
            <ReactMarkdown>
              {t(`podcastFreePlayDynamic2026.${promoName}.description`)}
            </ReactMarkdown>
          </DescriptionText>

          <ClaimYourEntryTitle>
            {t("podcastFreePlayDynamic2026.common.content.claimYourEntry")}
          </ClaimYourEntryTitle>
        </container-div>
        <Row>
          <Col xs={12} className="mx-auto">
            <FormWrapper ref={formRef} className={!isPublished ? "disabled" : ""}>
              <DynamicGoogleFormSubmitter
                formId="AKfycbyvXQrLcdNukqw9x-O5KEenG5ZxNcW6UTYRe8sV2Fs1PMoplc0NjlsgqZ1TU7NEe0tH" // Fix intro and image args
                sheetName={sheetName}
                formTextKey="podcastFreePlayDynamic2026.common"
                titles={false}
                values={{ Prize: spinPrize }}
                disabled={!isPublished}
                formFields={[
                  { id: "FirstName", label: "firstName", type: "text", required: true },
                  { id: "LastName", label: "lastName", type: "text", required: true },
                  { id: "Email", label: "email", type: "email", required: true },
                  { id: "PlayerID", label: "pgPlayerId", type: "text", required: false, placeholder: "pgPlayerIdPlaceholder" },
                ]}
              />
              {/* Custom Submit Button that will be moved inside form container via JS */}
              <CustomSubmitButtonContainer ref={customButtonRef}>
                <Button
                  onClick={handleCustomSubmit}
                  bgColor="var(--red)"
                  color="var(--white)"
                  hoverColor="var(--black)"
                  hoverBgColor="var(--white)"
                  size="large"
                  width="wide"
                  label={t("podcastFreePlayDynamic2026.common.content.spinNow")}
                  disabled={!isPublished}
                />
              </CustomSubmitButtonContainer>
            </FormWrapper>
          </Col>
        </Row>
        <Row className="small pt-4 pb-4">
          <Col xs={12} md={12} lg={8} className="mx-auto">
            <FormSmallPrint>
              <MarkdownWithRouter>
                {t("podcastFreePlayDynamic2026.common.content.formSmallPrint")}
              </MarkdownWithRouter>
            </FormSmallPrint>
          </Col>
        </Row>
        <Row className="pt-4 pb-4">
          <Col xs={12} md={12} lg={8} className="mx-auto">
              <RegisterText>
                <MarkdownWithRouter>
                  {t("podcastFreePlayDynamic2026.common.content.register")}
                </MarkdownWithRouter>
              </RegisterText>
          </Col>
        </Row>

        <container-div>
          <Row className="g-4 mb-4 mt-4">
            <Col xs={12} md={12} lg={10} className="mx-auto">
              <ContentCard
                bgImage={useResponsiveImageUrl(
                  "promotions/gaming-machines/win-a-million-2025/sparkle-box.png"
                )}
              >
                <CardHeader>
                  {t("podcastFreePlayDynamic2026.common.content.aboutTitle")}
                </CardHeader>
                <CardContent>
                  <ReactMarkdown>
                    {t("podcastFreePlayDynamic2026.common.content.aboutText")}
                  </ReactMarkdown>
                  <StyledButtonWrapper>
                    <Button
                      bgColor="#c9a961"
                      color="var(--black)"
                      hoverBgColor="var(--white)"
                      hoverColor="var(--black)"
                      label={t("button.learn-more")}
                      href={t("routes.aboutUs")}
                      width="wide"
                      size="large"
                    />
                  </StyledButtonWrapper>
                </CardContent>
              </ContentCard>
            </Col>
          </Row>
        </container-div>
      </MainContentDiv>
      <SpinWheelModal
        show={showWheel}
        onHide={() => setShowWheel(false)}
        onPrize={handlePrizeWon}
        data={wheelData}
        subscribeOnResult={false}
      />
    </PodcastFreePlayDiv>
  );
};

const PodcastFreePlayDiv = styled.div`
  --topspacing: 110px;
  --gradientheight: 40px;

  width: 100%;
  height: auto;
  background-color: var(--lightblack);
  color: var(--white);
  text-align: center;
  overflow-x: hidden;
  padding-bottom: 40px;

  .hero_image_container {
    img {
      margin-top: 2rem;
      margin-bottom: 2rem;
    }
  }

  .title-section {
    h2 {
      text-transform: uppercase;
      color: #f0cb43;
    }
    p {
      max-width: unset;
    }
  }

  .small {
    text-align: center;
  }

  strong {
    color: #f0cb43;
  }

  p,
  li {
    strong {
      color: #f0cb43;
    }
  }
`;

const ErrorDiv = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--lightblack);
  color: var(--white);

  h2 {
    color: #f0cb43;
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.2rem;
  }
`;

const MainTitle = styled.h2`
  color: #f0cb43;
  font-size: 2.5rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 1rem;
  letter-spacing: 0.1em;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const DescriptionText = styled.div`
  color: var(--white);
  font-size: 1.1rem;
  line-height: 1.6;
  text-align: center;
  max-width: 900px;
  margin: 0 auto 3rem;

  p {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #f0cb43;
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const ClaimYourEntryTitle = styled.h3`
  color: #f0cb43;
  font-size: 2rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FormSmallPrint = styled.div`
  color: var(--white);
  font-size: 0.9rem;
  line-height: 1.5;
  text-align: center;

  p {
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #f0cb43;
  }

  a {
    color: #f0cb43;
    text-decoration: underline;

    &:hover {
      color: var(--white);
    }
  }

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const RegisterText = styled.div`
  margin-top: 1rem;
  color: var(--white);
  line-height: 1.5;
  text-align: center;
  
  p {
    font-size: 1.3rem;
    margin-bottom: 0;
  }

  strong {
    color: #f0cb43;
  }

  a {
    color: #f0cb43;
    text-decoration: underline;

    &:hover {
      color: var(--white);
    }
  }

`;

const MainContentDiv = styled.div`
  padding-bottom: 1rem;
  background-color: var(--lightblack);

  @media (min-width: 768px) {
    padding: 0 1rem 1rem;
  }

  @media (min-width: 992px) {
    padding: 0 0 3rem;
  }

  height: auto;
  width: 100%;

  container-div {
    max-width: 1200px;
  }

  .row {
    display: flex;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
  }
`;

const ContentCard = styled.div`
  background: url(${(props) => props.bgImage}) no-repeat;
  background-size: cover;
  background-position: 70% center;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #d4af37 0%, #f4e4a6 50%, #d4af37 100%);
  color: var(--black);
  padding: 0.2rem 1.5rem;
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.75rem 1rem;
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
  color: var(--white);
  flex: 1;
  text-align: center;

  p {
    margin-bottom: 0.75rem;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #f0cb43;
    font-weight: 600;
  }

  ul,
  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    text-align: left;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }

  a {
    display: inline-block;
    margin-top: 1rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    font-size: 0.95rem;
  }
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;

  /* Hide the original form submit button - more specific selector */
  && .form-container button[type="submit"],
  && .form-container a.btn[type="submit"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
    position: absolute !important;
    left: -9999px !important;
  }

  /* Left align form labels */
  .form-container .form-label,
  .form-container label {
    text-align: left !important;
    justify-content: flex-start !important;
    align-items: flex-start !important;
    display: block !important;
  }

  /* Disabled form input styles */
  .form-container .form-control:disabled {
    background-color: #2a2a2a !important;
    color: #888 !important;
    border-color: #444 !important;
    cursor: not-allowed !important;
    opacity: 0.6;
  }

  .form-container .form-label,
  .form-container label {
    transition: opacity 0.2s ease;
  }

  /* Dim labels when form wrapper has disabled class */
  &.disabled .form-container .form-label,
  &.disabled .form-container label {
    opacity: 0.5;
  }

  /* Style the submit button with gold gradient (if it were visible) */
  .form-container button[type="submit"] {
    background: linear-gradient(
      135deg,
      #c9a961 0%,
      #f4e4a6 50%,
      #c9a961 100%
    ) !important;
    color: #000 !important;
    border: none !important;

    &:hover {
      background: linear-gradient(
        135deg,
        #b89551 0%,
        #e4d496 50%,
        #b89551 100%
      ) !important;
      color: #000 !important;
    }
  }
`;

const CustomSubmitButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2rem;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
  }
`;

const StyledButtonWrapper = styled.div`
  a {
    background: linear-gradient(
      135deg,
      #c9a961 0%,
      #f4e4a6 50%,
      #c9a961 100%
    ) !important;
    padding: 1rem 3.5rem !important;
    font-size: 1.15rem !important;
    border-radius: 10px !important;
    min-width: 250px !important;

    &:hover {
      transform: translateY(-2px) !important;
      box-shadow: 0 6px 16px rgba(201, 169, 97, 0.5) !important;
      background: linear-gradient(
        135deg,
        #c9a961 0%,
        #f4e4a6 50%,
        #c9a961 100%
      ) !important;
    }

    @media (max-width: 768px) {
      max-width: 350px !important;
      padding: 1rem 2rem !important;
      font-size: 1.05rem !important;
      min-width: unset !important;
    }
  }
`;

export default PodcastFreePlayDynamic2026;
